import React, { useRef, useEffect } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { motion, useAnimation, useInView } from 'framer-motion';
import PropTypes from 'prop-types';

/**
 * TextBox - A styled box component for highlighting text content
 * Follows project architecture with MUI theme integration and framer-motion animations
 */
const TextBox = ({
    children,
    variant = 'default',
    backgroundColor,
    textAlign = 'center',
    padding,
    margin,
    borderRadius,
    boxShadow,
    animate = true,
    sx = {},
    ...props
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const ref = useRef(null);
    const controls = useAnimation();
    const isInView = useInView(ref, { threshold: 0.1 });

    useEffect(() => {
        if (animate && isInView) {
            controls.start("visible");
        }
    }, [isInView, controls, animate]);

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };    // Variant styles
    const getVariantStyles = () => {
        switch (variant) {
            case 'highlighted':
                return {
                    backgroundColor: backgroundColor || theme.palette.primary.light,
                    color: theme.palette.text.primary,
                    border: `2px solid ${theme.palette.primary.main}`,
                };
            case 'subtle':
            default:
                return {
                    backgroundColor: backgroundColor || theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                };
        }
    };

    const defaultPadding = isMobile ? theme.spacing(2.5) : theme.spacing(3);
    const defaultMargin = theme.spacing(2, 0);
    const defaultBorderRadius = theme.spacing(1);
    const defaultBoxShadow = theme.shadows[2];

    return (
        <Box
            ref={ref}
            component={animate ? motion.div : 'div'}
            initial={animate ? "hidden" : undefined}
            animate={animate ? controls : undefined}
            variants={animate ? containerVariants : undefined}
            sx={{
                padding: padding || defaultPadding,
                margin: margin || defaultMargin,
                borderRadius: borderRadius || defaultBorderRadius,
                boxShadow: boxShadow || defaultBoxShadow,                textAlign: textAlign,
                fontFamily: theme.typography.fontFamily,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'default',                '&:hover': {
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: theme.shadows[8],
                },
                ...getVariantStyles(),
                ...sx
            }}
            {...props}
        >
            {typeof children === 'string' ? (
                <Typography
                    variant="body1"
                    sx={{
                        fontFamily: theme.typography.fontFamily,
                        lineHeight: 1.6,
                        fontSize: {
                            xs: theme.typography.pxToRem(14),
                            sm: theme.typography.pxToRem(16),
                            md: theme.typography.pxToRem(16),
                        },
                        fontWeight: theme.typography.fontWeightRegular,
                    }}
                >
                    {children}
                </Typography>
            ) : (
                children
            )}
        </Box>
    );
};

TextBox.propTypes = {
    children: PropTypes.node.isRequired,
    variant: PropTypes.oneOf(['subtle', 'highlighted']),
    backgroundColor: PropTypes.string,
    textAlign: PropTypes.oneOf(['left', 'center', 'right', 'justify']),
    padding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    margin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    borderRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    boxShadow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    animate: PropTypes.bool,
    sx: PropTypes.object,
};

export default TextBox;
